import { API_URL } from "./config";
import './MyForm.css'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { useCallback, useState, useEffect } from 'react'
import { Alert, Container } from 'react-bootstrap';

import token from './Token';
import {
    MdAdd,
    MdArrowDownward,
    MdArrowUpward,
    MdClose,
    MdDelete,
    MdDownload,
    MdEdit,
    MdSave,
} from 'react-icons/md';
import { Spinner } from 'react-bootstrap';

import moment from 'moment'

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text area' },
    { value: 'button', label: 'Button group' },
    { value: 'select', label: 'Select' },
    { value: 'check', label: 'Checkbox' },
    { value: 'label', label: 'Label / HTML' },
]

const createEmptyEditorField = () => ({
    q: '',
    t: 'text',
    a: '',
    c: [],
    optionsText: '',
})

const cloneForm = (form) => JSON.parse(JSON.stringify(Array.isArray(form) ? form : []))

const splitOptions = (value) => (
    String(value || '')
        .split(/\n|,/)
        .map((option) => option.trim())
        .filter(Boolean)
)

const fieldToEditorField = (field) => {
    const choices = Array.isArray(field.c) ? field.c : []

    return {
        q: field.q || '',
        t: field.t || 'text',
        a: field.a === undefined || field.a === null ? '' : field.a,
        c: choices,
        optionsText: choices.join('\n'),
    }
}

const normalizeEditorField = (field) => {
    const type = field.t || 'text'
    const normalized = {
        q: String(field.q || '').trim(),
        t: type,
    }

    if (type === 'label') {
        return normalized
    }

    if (type === 'check') {
        normalized.a = Boolean(field.a)
        return normalized
    }

    if (type === 'button' || type === 'select') {
        const choices = splitOptions(field.optionsText)
        normalized.c = choices
        normalized.a = choices.includes(field.a) ? field.a : choices[0]
        return normalized
    }

    normalized.a = field.a === undefined || field.a === null ? '' : String(field.a)
    return normalized
}

const getTemplateId = (template) => template?._id || template?.id || template?.name || ''

function MyForm() {
    const [loading, setLoading] = useState(false)
    const [formTemplates, setFormTemplates] = useState([])
    const [formName, setFormName] = useState('')
    const [data, setData] = useState([])
    const [pastResponses, setPastResponses] = useState([])
    const [showEditor, setShowEditor] = useState(false)
    const [editingTemplateId, setEditingTemplateId] = useState('')
    const [editingOriginalName, setEditingOriginalName] = useState('')
    const [editorName, setEditorName] = useState('')
    const [editorFields, setEditorFields] = useState([createEmptyEditorField()])
    const [editorError, setEditorError] = useState('')
    const [editorMessage, setEditorMessage] = useState('')
    const [savingTemplate, setSavingTemplate] = useState(false)
    const [deletingTemplate, setDeletingTemplate] = useState(false)

    useEffect(() => {
        const form = formTemplates.find((ft) => ft.name === formName)
        if (form) {
            setData(cloneForm(form.form))
        } else {
            setData([])
        }
    }, [formName, formTemplates])

    const selectedTemplate = formTemplates.find((ft) => ft.name === formName)

    const handleAnswerChange = (index, a) => {
        const newData = data.map((d, itemIndex) => {
            if (itemIndex === index) {
                return {
                    ...d,
                    a,
                }
            }
            return d
        })
        setData(newData)
    }

    const submitForm = async () => {
        setLoading(true)
        try {
            let jsonFormData = JSON.stringify(data)
            const body = new URLSearchParams({
                data: jsonFormData,
                formName,
            })

            await fetch(API_URL + '/savediary?token=' + token, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body,
            })
            await getPastResponses()
        } finally {
            setLoading(false)
        }
    }

    const getPastResponses = useCallback(async () => {
        const result = await fetch(API_URL + '/diary?token=' + token)
        const jsonResult = await result.json()
        setPastResponses(Array.isArray(jsonResult) ? jsonResult : [])
    }, [])

    const getFormTemplates = useCallback(async () => {
        const result = await fetch(API_URL + '/diaryTemplates?token=' + token)
        const jsonResult = await result.json()
        setFormTemplates(Array.isArray(jsonResult) ? jsonResult : [])
    }, [])

    const refreshPageData = useCallback(async () => {
        setLoading(true)
        try {
            await Promise.all([getPastResponses(), getFormTemplates()])
        } finally {
            setLoading(false)
        }
    }, [getPastResponses, getFormTemplates])

    const startNewTemplate = () => {
        setEditingTemplateId('')
        setEditingOriginalName('')
        setEditorName('')
        setEditorFields([createEmptyEditorField()])
        setEditorError('')
        setEditorMessage('')
        setShowEditor(true)
    }

    const startEditTemplate = (template) => {
        if (!template) {
            setEditorError('Select a form before editing.')
            setShowEditor(true)
            return
        }

        setEditingTemplateId(getTemplateId(template))
        setEditingOriginalName(template.name || '')
        setEditorName(template.name || '')
        setEditorFields(cloneForm(template.form).map(fieldToEditorField))
        setEditorError('')
        setEditorMessage('')
        setShowEditor(true)
    }

    const updateEditorField = (index, updates) => {
        setEditorFields((currentFields) => (
            currentFields.map((field, fieldIndex) => {
                if (fieldIndex !== index) {
                    return field
                }

                const nextField = {
                    ...field,
                    ...updates,
                }

                if (updates.t) {
                    if (updates.t === 'check') {
                        nextField.a = Boolean(nextField.a)
                    } else if (updates.t === 'button' || updates.t === 'select') {
                        const choices = splitOptions(nextField.optionsText)
                        nextField.a = choices.includes(nextField.a) ? nextField.a : choices[0] || ''
                    } else {
                        nextField.a = typeof nextField.a === 'string' ? nextField.a : ''
                    }
                }

                return nextField
            })
        ))
    }

    const addEditorField = () => {
        setEditorFields((currentFields) => [...currentFields, createEmptyEditorField()])
    }

    const removeEditorField = (index) => {
        setEditorFields((currentFields) => {
            if (currentFields.length === 1) {
                return [createEmptyEditorField()]
            }

            return currentFields.filter((_, fieldIndex) => fieldIndex !== index)
        })
    }

    const moveEditorField = (index, delta) => {
        setEditorFields((currentFields) => {
            const targetIndex = index + delta
            if (targetIndex < 0 || targetIndex >= currentFields.length) {
                return currentFields
            }

            const reorderedFields = [...currentFields]
            const [movedField] = reorderedFields.splice(index, 1)
            reorderedFields.splice(targetIndex, 0, movedField)
            return reorderedFields
        })
    }

    const buildTemplateFromEditor = () => {
        const name = editorName.trim()
        const normalizedFields = editorFields.map(normalizeEditorField)

        if (!name) {
            throw new Error('Form name is required.')
        }

        if (normalizedFields.length === 0) {
            throw new Error('Add at least one field.')
        }

        normalizedFields.forEach((field, index) => {
            if (!field.q) {
                throw new Error(`Field ${index + 1} needs a question or label.`)
            }

            if ((field.t === 'button' || field.t === 'select') && (!field.c || field.c.length === 0)) {
                throw new Error(`Field ${index + 1} needs at least one option.`)
            }
        })

        const duplicateName = formTemplates.some((template) => (
            template.name?.toLowerCase() === name.toLowerCase()
            && getTemplateId(template) !== editingTemplateId
        ))

        if (duplicateName) {
            throw new Error('Another form already uses this name.')
        }

        return {
            _id: editingTemplateId || undefined,
            name,
            form: normalizedFields,
        }
    }

    const saveFormTemplate = async () => {
        try {
            setSavingTemplate(true)
            setEditorError('')
            setEditorMessage('')

            const template = buildTemplateFromEditor()
            const body = new URLSearchParams({
                data: JSON.stringify(template),
                id: editingTemplateId,
                originalName: editingOriginalName,
            })

            const result = await fetch(API_URL + '/saveDiaryTemplate?token=' + token, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body,
            })

            if (!result.ok) {
                throw new Error('The server did not save the form template.')
            }

            await getFormTemplates()
            setFormName(template.name)
            setEditingTemplateId(getTemplateId(template))
            setEditingOriginalName(template.name)
            setEditorMessage('Form template saved.')
        } catch (error) {
            setEditorError(error.message || 'Could not save form template.')
        } finally {
            setSavingTemplate(false)
        }
    }

    const deleteFormTemplate = async (template) => {
        if (!template) {
            setEditorError('Select a form before deleting.')
            setShowEditor(true)
            return
        }

        const confirmed = window.confirm(`Delete "${template.name}"? Existing submitted responses will stay in Past Responses.`)
        if (!confirmed) {
            return
        }

        try {
            setDeletingTemplate(true)
            setEditorError('')
            setEditorMessage('')

            const templateId = getTemplateId(template)
            const result = await fetch(
                API_URL
                + '/deleteDiaryTemplate?token='
                + token
                + '&id='
                + encodeURIComponent(templateId)
                + '&name='
                + encodeURIComponent(template.name || '')
            )

            if (!result.ok) {
                throw new Error('The server did not delete the form template.')
            }

            await getFormTemplates()
            if (formName === template.name) {
                setFormName('')
                setData([])
            }
            setShowEditor(false)
            setEditorMessage('Form template deleted.')
        } catch (error) {
            setEditorError(error.message || 'Could not delete form template.')
            setShowEditor(true)
        } finally {
            setDeletingTemplate(false)
        }
    }

    useEffect(() => {
        document.title = 'Ordo Declaration Services'
        refreshPageData()
    }, [refreshPageData])


    return (
        <div className='AppRootDesktop'>
            <div className='AppRootInnerOverlay'>
                <Container fluid className="myform-page">
                    <div className="myform-action-bar">
                        <h1>Ordo Declaration Services</h1>
                        <div className="myform-action-controls">
                            <Button variant="outline-light" onClick={startNewTemplate}>
                                <MdAdd /> New Form
                            </Button>
                            <Button
                                variant="outline-light"
                                onClick={() => startEditTemplate(selectedTemplate)}
                                disabled={!selectedTemplate}
                            >
                                <MdEdit /> Edit
                            </Button>
                            <Button
                                variant="outline-danger"
                                onClick={() => deleteFormTemplate(selectedTemplate)}
                                disabled={!selectedTemplate || deletingTemplate}
                            >
                                <MdDelete /> Delete
                            </Button>
                            {loading && <Spinner animation="border" variant="primary" />}
                        </div>
                    </div>

                    <div className='MyFormContainer'>
                        {editorMessage && !showEditor && (
                            <Alert variant="success" onClose={() => setEditorMessage('')} dismissible>
                                {editorMessage}
                            </Alert>
                        )}

                        {showEditor && (
                            <div className="form-editor-card">
                                <div className="form-editor-header">
                                    <div>
                                        <h2>{editingTemplateId ? 'Edit form' : 'Create form'}</h2>
                                    </div>
                                    <Button
                                        variant="outline-light"
                                        className="icon-button"
                                        onClick={() => setShowEditor(false)}
                                        title="Close editor"
                                    >
                                        <MdClose />
                                    </Button>
                                </div>

                                {editorError && <Alert variant="danger">{editorError}</Alert>}
                                {editorMessage && <Alert variant="success">{editorMessage}</Alert>}

                                <Form.Group className="form-editor-name-control">
                                    <Form.Label>Form name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editorName}
                                        onChange={(e) => setEditorName(e.target.value)}
                                        placeholder="Daily reflection"
                                    />
                                </Form.Group>

                                <div className="editor-fields-list">
                                    {editorFields.map((field, index) => (
                                        <div className="editor-field-row" key={index}>
                                            <div className="editor-field-row-header">
                                                <span>Field {index + 1}</span>
                                                <div className="editor-row-actions">
                                                    <Button
                                                        variant="outline-light"
                                                        className="icon-button"
                                                        onClick={() => moveEditorField(index, -1)}
                                                        disabled={index === 0}
                                                        title="Move up"
                                                    >
                                                        <MdArrowUpward />
                                                    </Button>
                                                    <Button
                                                        variant="outline-light"
                                                        className="icon-button"
                                                        onClick={() => moveEditorField(index, 1)}
                                                        disabled={index === editorFields.length - 1}
                                                        title="Move down"
                                                    >
                                                        <MdArrowDownward />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        className="icon-button"
                                                        onClick={() => removeEditorField(index)}
                                                        title="Remove field"
                                                    >
                                                        <MdDelete />
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="editor-field-grid">
                                                <Form.Group>
                                                    <Form.Label>Type</Form.Label>
                                                    <Form.Select
                                                        value={field.t}
                                                        onChange={(e) => updateEditorField(index, { t: e.target.value })}
                                                    >
                                                        {FIELD_TYPES.map((fieldType) => (
                                                            <option key={fieldType.value} value={fieldType.value}>
                                                                {fieldType.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group className="editor-question-control">
                                                    <Form.Label>{field.t === 'label' ? 'HTML label' : 'Question'}</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={field.t === 'label' ? 4 : 2}
                                                        value={field.q}
                                                        onChange={(e) => updateEditorField(index, { q: e.target.value })}
                                                        placeholder={field.t === 'label' ? '<h3>Heading</h3><p>Instructions</p>' : 'What would you like to ask?'}
                                                    />
                                                </Form.Group>
                                            </div>

                                            {(field.t === 'button' || field.t === 'select') && (
                                                <div className="editor-field-grid">
                                                    <Form.Group className="editor-question-control">
                                                        <Form.Label>Options</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            value={field.optionsText}
                                                            onChange={(e) => updateEditorField(index, { optionsText: e.target.value })}
                                                            placeholder={'One option per line, or comma separated'}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Label>Default</Form.Label>
                                                        <Form.Select
                                                            value={field.a || ''}
                                                            onChange={(e) => updateEditorField(index, { a: e.target.value })}
                                                        >
                                                            <option value="">Use first option</option>
                                                            {splitOptions(field.optionsText).map((option) => (
                                                                <option key={option} value={option}>{option}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </div>
                                            )}

                                            {(field.t === 'text' || field.t === 'textarea') && (
                                                <Form.Group>
                                                    <Form.Label>Default answer</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={field.a || ''}
                                                        onChange={(e) => updateEditorField(index, { a: e.target.value })}
                                                        placeholder="Optional"
                                                    />
                                                </Form.Group>
                                            )}

                                            {field.t === 'check' && (
                                                <Form.Check
                                                    label="Checked by default"
                                                    checked={Boolean(field.a)}
                                                    onChange={(e) => updateEditorField(index, { a: e.target.checked })}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="form-editor-footer">
                                    <Button variant="outline-light" onClick={addEditorField}>
                                        <MdAdd /> Add Field
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={saveFormTemplate}
                                        disabled={savingTemplate}
                                    >
                                        {savingTemplate ? <Spinner animation="border" size="sm" /> : <MdSave />}
                                        Save Form
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Form Selector Card */}
                        <div className='form-selector-card'>
                            <h2>Select a form</h2>
                            <Form.Select
                                value={formName}
                                onChange={(e) => {
                                    setFormName(e.target.value)
                                }}
                            >
                                <option value=''>Select a form...</option>
                                {formTemplates.map((ft, i) => (
                                    <option key={getTemplateId(ft) || i} value={ft.name}>{ft.name}</option>
                                ))}
                            </Form.Select>
                        </div>

                        {/* QA Form Card */}
                        {data.length > 0 && (
                            <div className="qa-form-card">
                                {data.map((q, i) => (
                                    <div key={i} className={`QAFormRow ${q.t === 'label' ? 'QAFormRowLabel' : ''}`}>
                                        {q.t !== 'label' && <div className="QAFormQuestion">{q.q}</div>}
                                        {q.t === 'label' ? (
                                            <div className="QAFormLabel" dangerouslySetInnerHTML={{ __html: q.q }}></div>
                                        ) : (
                                            <div className="QAFormAnswer">
                                                {q.t === 'text' &&
                                                    <Form.Control type='text' onChange={(e) => {
                                                        handleAnswerChange(i, e.target.value)
                                                    }} value={q.a}></Form.Control>
                                                }
                                                {q.t === 'textarea' &&
                                                    <Form.Control as='textarea' rows={3} onChange={(e) => {
                                                        handleAnswerChange(i, e.target.value)
                                                    }} value={q.a}></Form.Control>
                                                }
                                                {q.t === 'button' &&
                                                    <ButtonGroup>
                                                        {(q.c || []).map((c, j) => (
                                                            <Button key={j} onClick={() => {
                                                                handleAnswerChange(i, c)
                                                            }} variant={q.a === c ? 'primary' : 'outline-secondary'}>{c}</Button>
                                                        ))}
                                                    </ButtonGroup>
                                                }
                                                {q.t === 'select' &&
                                                    <Form.Select onChange={(e) => {
                                                        handleAnswerChange(i, e.target.value)
                                                    }} value={q.a}>
                                                        {(q.c || []).map((c, j) => (
                                                            <option key={j} value={c}>{c}</option>
                                                        ))}
                                                    </Form.Select>
                                                }
                                                {q.t === 'check' &&
                                                    <Form.Check onChange={(e) => {
                                                        handleAnswerChange(i, e.target.checked)
                                                    }} checked={Boolean(q.a)}></Form.Check>
                                                }
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div className='QAFormSubmit'>
                                    {loading && <Spinner animation="border" variant="primary" />}
                                    {!loading && (
                                        <Button variant="success" onClick={submitForm}>
                                            Submit
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Past Responses Card */}
                        <div className='past-responses-card'>
                            <h2>Past Responses</h2>
                            {pastResponses.length === 0 ? (
                                <div className="empty-state">
                                    <p>No past responses yet</p>
                                </div>
                            ) : (
                                <table className="bordered-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Form</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pastResponses.map((pr, i) => (
                                            <tr key={i}>
                                                <td>{moment(pr.date).format('MMMM Do YYYY, h:mm:ss a')}</td>
                                                <td>{pr.name}</td>
                                                <td>
                                                    <div className="table-actions">
                                                        <MdDownload
                                                            onClick={() => window.location.href = API_URL + '/diarypdf?token=' + token + '&id=' + pr._id}
                                                            title="Download PDF"
                                                        />
                                                        <MdDelete
                                                            className="delete-icon"
                                                            onClick={async () => {
                                                                await fetch(API_URL + '/deletediary?token=' + token + '&id=' + pr._id)
                                                                getPastResponses()
                                                            }}
                                                            title="Delete"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </Container>
            </div>
        </div>
    )
}

export default MyForm;
